import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Mail, Phone, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

// ── Frontend-only email delivery ─────────────────────────────────────────────
// Where consultation requests are delivered. CHANGE THIS to your real inbox.
const CONTACT_EMAIL = "hello@team-x.in";
// Optional: set REACT_APP_WEB3FORMS_KEY in frontend/.env to use Web3Forms instead
// of FormSubmit (both are free, no backend required).
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", company: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // spam trap

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (honeypot) return; // bot detected — silently ignore

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      let ok = false;

      // 1) Try Web3Forms (if a key is configured)
      if (WEB3FORMS_KEY) {
        try {
          const fd = new FormData();
          fd.append("access_key", WEB3FORMS_KEY);
          fd.append("subject", `New consultation request from ${form.name}`);
          fd.append("from_name", "Team-X Website");
          fd.append("name", form.name);
          fd.append("email", form.email);
          fd.append("company", form.company || "-");
          fd.append("message", form.message);
          const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body: fd });
          ok = (await res.json().catch(() => ({}))).success === true;
        } catch {
          ok = false;
        }
      }

      // 2) Fallback to FormSubmit (no key needed)
      if (!ok) {
        const fd = new FormData();
        fd.append("_subject", `New consultation request from ${form.name}`);
        fd.append("_template", "table");
        fd.append("_captcha", "false");
        fd.append("name", form.name);
        fd.append("email", form.email);
        fd.append("company", form.company || "-");
        fd.append("message", form.message);
        const res = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(CONTACT_EMAIL)}`, {
          method: "POST",
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        ok = res.ok && (data.success === "true" || data.success === true);
      }

      if (!ok) throw new Error("send-failed");

      setForm(EMPTY);
      setSent(true);
      toast.success("Message sent! Our experts will reach out shortly.");
    } catch (err) {
      toast.error(`Couldn't send right now. Please email us at ${CONTACT_EMAIL}.`);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "border-white/10 bg-white/5 text-white placeholder:text-zinc-600 focus-visible:border-brand focus-visible:ring-brand/20 h-12";

  return (
    <section id="contact" className="relative z-10 border-t border-white/10 bg-ink-950 py-24 md:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal><Overline>Contact</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 max-w-xl font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl md:text-5xl">
                Let's engineer what's next, together.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400">
                Tell us about your goals and challenges. Our experts will get back to you to scope a
                tailored engagement.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-10 flex flex-col gap-4">
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-5 transition-colors duration-300 hover:border-brand/40">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-brand/10 text-brand"><Mail className="h-5 w-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-zinc-500">Email</span>
                  <span className="font-medium text-white">{CONTACT_EMAIL}</span>
                </span>
              </a>
              <a href="tel:+10000000000" data-testid="contact-phone" className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-5 transition-colors duration-300 hover:border-brand/40">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-brand/10 text-brand"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-zinc-500">Phone</span>
                  <span className="font-medium text-white">+91 90003 00117</span>
                </span>
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            {sent ? (
              <div
                className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-brand/30 bg-white/[0.02] p-10 text-center"
                data-testid="contact-success"
              >
                <span className="mb-6 grid h-16 w-16 place-items-center rounded-full bg-brand/10 text-brand glow-cyan">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white">Message sent</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-zinc-400">
                  Thank you for reaching out. Our team will get back to you shortly at the email you provided.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:border-brand/50"
                >
                  Send another message
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-8" data-testid="contact-form" noValidate>
                <div className="grid gap-5">
                  {/* honeypot (hidden from users) */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <div className="grid gap-2">
                    <label htmlFor="c-name" className="text-sm font-medium text-zinc-300">Full name</label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" className={inputClass} />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <label htmlFor="c-email" className="text-sm font-medium text-zinc-300">Work email</label>
                      <Input id="c-email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="jane@company.com" className={inputClass} />
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="c-company" className="text-sm font-medium text-zinc-300">Company</label>
                      <Input id="c-company" data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Company Inc." className={inputClass} />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="c-message" className="text-sm font-medium text-zinc-300">How can we help?</label>
                    <Textarea id="c-message" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="Tell us about your project or challenge..." rows={5} className="border-white/10 bg-white/5 text-white placeholder:text-zinc-600 focus-visible:border-brand focus-visible:ring-brand/20" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-ink-950 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 glow-cyan"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Request Consultation
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
